<template>
  <div
    v-if="entry"
    class="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/10 p-4 mb-4">
    <!-- Header: icon + label + purpose + toggle -->
    <button
      @click="toggle"
      class="w-full flex items-start gap-3 text-left"
      :aria-expanded="expanded">
      <svg class="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div class="flex-1">
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-semibold font-heading text-primary-900 dark:text-primary-200">
            {{ entry.label }}
          </span>
          <svg
            class="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 transition-transform"
            :class="{ 'rotate-180': expanded }"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <p class="text-sm text-gray-700 dark:text-gray-300 mt-1 leading-relaxed">
          {{ entry.purpose }}
        </p>
      </div>
    </button>

    <!-- Body: structured sections -->
    <div v-if="expanded" class="mt-4 space-y-3 pl-8">
      <section v-if="entry.inputs && entry.inputs.length">
        <h4 class="text-xs uppercase tracking-wide text-primary-700 dark:text-primary-400 mb-1">
          What it depends on
        </h4>
        <ul class="space-y-1">
          <li v-for="(item, i) in entry.inputs" :key="'in-' + i" class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            <span class="text-primary-400 mr-1">•</span>{{ item }}
          </li>
        </ul>
      </section>

      <section v-if="entry.process && entry.process.length">
        <h4 class="text-xs uppercase tracking-wide text-primary-700 dark:text-primary-400 mb-1">
          What happens here
        </h4>
        <ul class="space-y-1">
          <li v-for="(item, i) in entry.process" :key="'pr-' + i" class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            <span class="text-primary-400 mr-1">•</span>{{ item }}
          </li>
        </ul>
      </section>

      <section v-if="entry.outputs && entry.outputs.length">
        <h4 class="text-xs uppercase tracking-wide text-primary-700 dark:text-primary-400 mb-1">
          What it produces
        </h4>
        <ul class="space-y-1">
          <li v-for="(item, i) in entry.outputs" :key="'out-' + i" class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            <span class="text-primary-400 mr-1">•</span>{{ item }}
          </li>
        </ul>
      </section>

      <section v-if="entry.gotchas && entry.gotchas.length">
        <h4 class="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1">
          Heads up
        </h4>
        <ul class="space-y-1">
          <li v-for="(item, i) in entry.gotchas" :key="'go-' + i" class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            <span class="text-amber-400 mr-1">•</span>{{ item }}
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { stepDocs } from '~/javascript/data/page_optimization_step_docs.js'

const props = defineProps({
  stepKey: { type: String, required: true }
})

const entry = computed(() => stepDocs[props.stepKey] || null)

const STORAGE_PREFIX = 'pageOpt:explainer:'

const readPersisted = (key) => {
  try {
    return localStorage.getItem(STORAGE_PREFIX + key) !== 'collapsed'
  } catch (_e) {
    return true
  }
}

const writePersisted = (key, isExpanded) => {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, isExpanded ? 'expanded' : 'collapsed')
  } catch (_e) {
    // localStorage unavailable (private browsing, etc.) — silently no-op
  }
}

const expanded = ref(readPersisted(props.stepKey))

// When the step changes (user switches tabs), re-read persistence for the new key
watch(() => props.stepKey, (next) => {
  expanded.value = readPersisted(next)
})

const toggle = () => {
  expanded.value = !expanded.value
  writePersisted(props.stepKey, expanded.value)
}
</script>
